/**
 * Sleep Karma – Cart Drawer Enhancements (v2-lite)
 *
 * Hooks into the Wonder theme's cart-drawer web component and PubSub system
 * to add: live community counter and re-initialization after Shopify's
 * Section Rendering API swaps the drawer innerHTML.
 *
 * No external dependencies — native browser APIs only.
 */

(function () {
  'use strict';

  var JUST_ADDED_MS = 1400;
  var justAddedTimer = 0;

  /* ── Just-added line cue ───────────────────────────────────────── */

  /**
   * /cart/add.js returns either one line item (`key`) or `{ items: [...] }`
   * when the request used an items array. Both shapes include `sections`.
   */
  function addedLineKeys(state) {
    var keys = [];
    if (!state || typeof state !== 'object') return keys;
    if (state.key) keys.push(String(state.key));
    if (Array.isArray(state.items)) {
      state.items.forEach(function (item) {
        if (item && item.key) keys.push(String(item.key));
      });
    }
    return keys;
  }

  function addedVariantIds(state) {
    var ids = [];
    function push(value) {
      if (value === undefined || value === null || value === '') return;
      ids.push(String(value));
    }
    if (!state || typeof state !== 'object') return ids;
    if (!Array.isArray(state.items)) {
      push(state.variant_id);
      push(state.id);
    } else {
      state.items.forEach(function (item) {
        if (!item) return;
        push(item.variant_id);
        push(item.id);
      });
    }
    return ids;
  }

  function findAddedLines(root, state) {
    var items = root.querySelectorAll('.sk-cart-item[data-line-key]');
    var keys = addedLineKeys(state);
    var matches = [];
    if (keys.length) {
      var keySet = {};
      keys.forEach(function (key) { keySet[key] = true; });
      items.forEach(function (el) {
        if (keySet[el.getAttribute('data-line-key')]) matches.push(el);
      });
    }
    if (matches.length) return matches;

    var ids = addedVariantIds(state);
    if (!ids.length) return matches;
    var idSet = {};
    ids.forEach(function (id) { idSet[id] = true; });
    var candidates = [];
    items.forEach(function (el) {
      if (idSet[el.getAttribute('data-variant-id')]) candidates.push(el);
    });
    if (candidates.length === 1) return candidates;
    return [];
  }

  function scrollLineIntoDrawer(line, reduceMotion) {
    var scroller = line.closest('.wt-cart__drawer__body');
    if (!scroller) return;
    var itemRect = line.getBoundingClientRect();
    var box = scroller.getBoundingClientRect();
    if (itemRect.top >= box.top && itemRect.bottom <= box.bottom) return;
    var delta = itemRect.top - box.top - 12;
    scroller.scrollTo({
      top: scroller.scrollTop + delta,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }

  function highlightJustAdded(root, state) {
    if (!root || !state) return;
    var lines = findAddedLines(root, state);
    if (!lines.length) return;

    root.querySelectorAll('.sk-cart-item--just-added').forEach(function (el) {
      el.classList.remove('sk-cart-item--just-added');
    });
    lines.forEach(function (el) {
      el.classList.add('sk-cart-item--just-added');
    });

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollLineIntoDrawer(lines[0], reduceMotion);

    var live = root.querySelector('#CartDrawer-LiveRegionText');
    if (live) {
      var nameEl = lines[0].querySelector('.cart-item__name');
      var name = nameEl ? nameEl.textContent.replace(/\s+/g, ' ').trim() : '';
      var chip = lines[0].querySelector('.sk-cart-item__just-added');
      var label = chip ? chip.textContent.replace(/\s+/g, ' ').trim() : 'Just added';
      live.textContent = name ? label + ': ' + name : label;
    }

    window.clearTimeout(justAddedTimer);
    var snapshot = lines.slice();
    justAddedTimer = window.setTimeout(function () {
      snapshot.forEach(function (el) {
        if (el.isConnected) el.classList.remove('sk-cart-item--just-added');
      });
      if (live && live.isConnected) live.textContent = '';
    }, JUST_ADDED_MS);
  }

  /**
   * PDP and page ATC paths call <cart-drawer>.renderContents() after
   * /cart/add.js. Patch that method so the matching row is marked once
   * the drawer HTML has been swapped in. Quantity edits use a different
   * update path and do not come through here.
   */
  function patchCartDrawerHighlight() {
    var Ctor = window.customElements && customElements.get('cart-drawer');
    if (!Ctor || Ctor.prototype._skJustAddedPatched) return false;
    var original = Ctor.prototype.renderContents;
    if (typeof original !== 'function') return false;
    Ctor.prototype._skJustAddedPatched = true;
    Ctor.prototype.renderContents = function (parsedState, isClosedCart) {
      original.call(this, parsedState, isClosedCart);
      var willOpen = isClosedCart !== false;
      var drawer = this;
      window.setTimeout(function () {
        highlightJustAdded(drawer, parsedState);
      }, willOpen ? 320 : 40);
    };
    return true;
  }

  document.addEventListener('sk:cart-line-added', function (event) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !event.detail) return;
    window.setTimeout(function () {
      highlightJustAdded(drawer, event.detail);
    }, 40);
  });

  /* ── Live Community Counter ────────────────────────────────────── */

  /**
   * Computes an hour-based deterministic "X joined this hour" count
   * once per page load. The value stays fixed for the entire session
   * so users never see it flicker or change.
   */
  var cachedLiveCount = (function () {
    var h = new Date().getHours();
    var base = ((h * 7 + 13) % 26) + 5;
    var jitter = Math.floor(Math.random() * 5) - 2;
    return Math.max(5, Math.min(30, base + jitter));
  })();

  function updateLiveCount() {
    var el = document.querySelector('[data-sk-live-count]');
    if (!el) return;
    el.textContent = cachedLiveCount;
  }

  /* ── Drawer Open Observer ──────────────────────────────────────── */

  /**
   * Watches the <cart-drawer> element for the `open` attribute.
   * When the drawer opens, refreshes the live count.
   */
  function observeDrawerOpen() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].type === 'attributes' && mutations[i].attributeName === 'open') {
          if (cartDrawer.hasAttribute('open')) {
            updateLiveCount();
          }
        }
      }
    });

    observer.observe(cartDrawer, { attributes: true });
  }

  /* ── Section Re-render Observer ────────────────────────────────── */

  /**
   * The Wonder theme's CartDrawerItems.updateQuantity() calls
   * renderContents() which replaces the innerHTML of .drawer__inner.
   * We observe #CartDrawer for child-list mutations and re-run
   * any enhancements that inject into the re-rendered DOM.
   */
  function observeSectionRerender() {
    var drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;

    var observer = new MutationObserver(function () {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.hasAttribute('open')) {
        setTimeout(function () {
          updateLiveCount();
        }, 100);
      }
    });

    observer.observe(drawer, { childList: true, subtree: true });
  }

  /* ── Boot ──────────────────────────────────────────────────────── */

  function init() {
    patchCartDrawerHighlight();
    observeDrawerOpen();
    observeSectionRerender();
  }

  patchCartDrawerHighlight();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
